import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Send, Eye, PenLine } from 'lucide-react';
import API, { formatError } from '../utils/api';
import { toast } from 'sonner';
import HtmlFieldPlacer from '../components/HtmlFieldPlacer';
import { substituteVariables, variableLabel } from '../utils/templateVars';

export default function UseTemplate() {
  const { templateId } = useParams();
  const navigate = useNavigate();

  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [step, setStep] = useState(1); // 1: variables, 2: fields & send

  const [variableValues, setVariableValues] = useState({});
  const [fields, setFields] = useState([]);
  const [recipient, setRecipient] = useState({ name: '', email: '' });
  const [envelope, setEnvelope] = useState({ title: '', message: '' });

  useEffect(() => {
    API.get(`/templates/${templateId}`)
      .then(r => {
        const t = r.data;
        setTemplate(t);
        setEnvelope({ title: t.name, message: '' });
        const vars = t.variables || [];
        const initial = {};
        vars.forEach(v => { initial[v] = ''; });
        setVariableValues(initial);
        setFields(t.fields || []);
      })
      .catch(() => { toast.error('Template not found'); navigate('/templates'); })
      .finally(() => setLoading(false));
  }, [templateId, navigate]);

  const previewHtml = useMemo(
    () => substituteVariables(template?.content_html || '', variableValues),
    [template, variableValues]
  );

  const detectedVars = template?.variables || [];

  const handleSend = async () => {
    if (!recipient.name || !recipient.email) {
      toast.error('Recipient name and email are required');
      return;
    }
    if (!envelope.title) {
      toast.error('Document title is required');
      return;
    }
    setSending(true);
    try {
      const r = await API.post(`/templates/${templateId}/use`, {
        variable_values: variableValues,
        recipient_name: recipient.name,
        recipient_email: recipient.email,
        title: envelope.title,
        message: envelope.message,
        fields,
        send: true,
      });
      toast.success('Document sent for signing!');
      navigate(`/envelopes/${r.data.envelope_id}`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-6 h-6 border-2 border-blue-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 fade-in max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/templates')} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-sm">
            <ChevronLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Use Template: {template.name}
            </h1>
            <p className="text-sm text-slate-500">Fill in variables, preview, and send for signing</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {step === 2 && (
            <button
              onClick={() => setStep(1)}
              className="px-3 py-2 border border-slate-300 text-slate-600 rounded-sm text-sm hover:bg-slate-50"
            >
              ← Back
            </button>
          )}
          {step === 1 ? (
            <button
              onClick={() => setStep(2)}
              className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-sm text-sm font-medium"
              data-testid="continue-to-fields-btn"
            >
              Continue <PenLine size={15} />
            </button>
          ) : (
            <button
              onClick={handleSend}
              disabled={sending}
              className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-sm text-sm font-medium disabled:opacity-60"
              data-testid="send-document-btn"
            >
              <Send size={15} /> {sending ? 'Sending…' : 'Send for Signing'}
            </button>
          )}
        </div>
      </div>

      {/* Step tabs */}
      <div className="flex gap-1 mb-6">
        {[
          { n: 1, label: 'Fill Variables' },
          { n: 2, label: 'Fields & Send' },
        ].map(s => (
          <button
            key={s.n}
            onClick={() => setStep(s.n)}
            className={`px-4 py-2 rounded-sm text-sm font-medium transition-colors ${
              step === s.n ? 'bg-blue-700 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
            }`}
          >
            {s.n}. {s.label}
          </button>
        ))}
      </div>

      {step === 1 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 rounded-sm p-5 space-y-4">
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <Eye size={15} /> Variable Values
            </h3>
            {detectedVars.length === 0 ? (
              <p className="text-sm text-slate-400">No variables in this template.</p>
            ) : (
              detectedVars.map(v => (
                <div key={v}>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{variableLabel(v)}</label>
                  <input
                    type="text"
                    value={variableValues[v] || ''}
                    onChange={e => setVariableValues(p => ({ ...p, [v]: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={`Enter ${variableLabel(v).toLowerCase()}`}
                    data-testid={`var-input-${v}`}
                  />
                </div>
              ))
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div className="px-4 py-2 border-b border-slate-200 bg-slate-50">
              <h3 className="text-sm font-semibold text-slate-700">Live Preview</h3>
            </div>
            <div
              className="document-preview prose prose-sm max-w-none p-6 min-h-[400px]"
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Document Title *</label>
              <input
                type="text"
                value={envelope.title}
                onChange={e => setEnvelope(p => ({ ...p, title: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Recipient Name *</label>
              <input
                type="text"
                value={recipient.name}
                onChange={e => setRecipient(p => ({ ...p, name: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="recipient-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Recipient Email *</label>
              <input
                type="email"
                value={recipient.email}
                onChange={e => setRecipient(p => ({ ...p, email: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="recipient-email-input"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Message to Signer (optional)</label>
            <textarea
              value={envelope.message}
              onChange={e => setEnvelope(p => ({ ...p, message: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="Please review and sign this document…"
            />
          </div>

          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Place Signing Fields</h3>
            <p className="text-xs text-slate-500 mb-4">Select a field type, then click on the document to place it.</p>
            <HtmlFieldPlacer html={previewHtml} fields={fields} onChange={setFields} />
          </div>
        </div>
      )}
    </div>
  );
}
